// A company's payment methods (Settings, Invoicing and Payments, Payment
// methods and fees): the built-in list, the label for a stored id, the Storm
// Mode completion choices, the Mark Paid options and the custom id rules.
//
// The list is GET /api/settings/payment-methods, each { id, name, enabled,
// isBuiltIn } and, for a deleted custom method, deleted: true. A deleted
// method stays in the list so its name still labels the stops paid with it.
// A stop's payment_method is the method id; Square is the one method with its
// own flow (invoiced), and it is recognised by exact id only.
//
// The id rules match server/services/paymentMethods.js, which applies them
// again on save.

export const DEFAULT_PAYMENT_METHODS = [
  { id: 'square', name: 'Square', enabled: true, isBuiltIn: true },
  { id: 'venmo', name: 'Venmo', enabled: true, isBuiltIn: true },
  { id: 'cash', name: 'Cash', enabled: true, isBuiltIn: true },
  { id: 'cashapp', name: 'Cash App', enabled: true, isBuiltIn: true },
  { id: 'zelle', name: 'Zelle', enabled: true, isBuiltIn: true },
  { id: 'check', name: 'Check', enabled: true, isBuiltIn: true },
];

const BUILT_IN_IDS = DEFAULT_PAYMENT_METHODS.map((m) => m.id);
const RESERVED_IDS = ['comp', 'paid', 'not_paid', 'unpaid', 'invoice_sent'];
const MAX_ID_LENGTH = 50;

// A stored method id with the legacy Storm Mode button suffix removed
// ('venmo_paid' was once stored for 'venmo').
export const methodBaseId = (id) => String(id || '').toLowerCase().replace(/_(not_)?paid$/, '');

export const isSquare = (id) => methodBaseId(id) === 'square';

// The name to show for a stored method id: the company's name for it (deleted
// or off included), else the built-in name, else the id with underscores as
// spaces. Never a guess such as "Cash". '' for no method; 'Comp' for a comped
// stop, which Storm Mode records with the method 'comp'.
export const methodLabel = (methods, id) => {
  if (!id) return '';
  const list = Array.isArray(methods) ? methods : [];
  const base = methodBaseId(id);
  if (base === 'comp') return 'Comp';
  const found = list.find((m) => m && m.id === id) || list.find((m) => m && m.id === base)
    || DEFAULT_PAYMENT_METHODS.find((m) => m.id === base);
  if (found && found.name) return found.name;
  return base.replace(/_/g, ' ');
};

const isOn = (m) => Boolean(m && m.enabled && !m.deleted);

// Storm Mode completion: every method that is on, other than Square (which
// has its own invoice section), in list order. Each gets a Paid and a Not Paid
// button.
export const completionChoices = (methods) => (Array.isArray(methods) ? methods : [])
  .filter((m) => isOn(m) && !isSquare(m.id))
  .map((m) => ({ id: m.id, name: m.name || m.id, isBuiltIn: BUILT_IN_IDS.includes(m.id) }));

// Mark Paid options: every method that is on, Square included. A record that
// already carries a method that is now off or deleted keeps it as an option
// marked "(off)", so opening Mark Paid never silently changes it.
export const markPaidOptions = (methods, currentId) => {
  const list = Array.isArray(methods) ? methods : [];
  const options = list.filter(isOn).map((m) => ({ id: m.id, name: m.name || m.id, off: false }));
  const current = currentId ? methodBaseId(currentId) : '';
  if (current && current !== 'comp' && !options.some((o) => o.id === current)) {
    options.push({ id: current, name: `${methodLabel(list, current)} (off)`, off: true });
  }
  return options;
};

// The customer form's Preferred method options: every method that is on,
// Square left out when the company has no active Square connection
// (squareAvailable false, from GET /api/settings/preferred-method). A
// customer whose preferred method is now off, deleted or Square without a
// connection keeps it as an option marked "(off)", with the stored id exactly
// as saved, so opening the form never silently changes it.
export const preferredMethodOptions = (methods, currentId, { squareAvailable = true } = {}) => {
  const list = Array.isArray(methods) ? methods : [];
  const options = list.filter((m) => isOn(m) && (squareAvailable || !isSquare(m.id)))
    .map((m) => ({ id: m.id, name: m.name || m.id, off: false }));
  if (currentId && !options.some((o) => o.id === currentId)) {
    options.push({ id: currentId, name: `${methodLabel(list, currentId)} (off)`, off: true });
  }
  return options;
};

// A new customer's or property's method: the first option, so Square only
// when the company can take it. The server applies the same default
// (server/services/preferredMethod.js) and answers Cash when nothing is on.
export const preferredMethodDefault = (methods, squareAvailable = true) => {
  const first = preferredMethodOptions(methods, '', { squareAvailable })[0];
  return first ? first.id : 'cash';
};

// The method a Mark Paid form opens on: the record's own, else Cash when it
// is on, else the first method that is on.
export const markPaidDefault = (methods, currentId) => {
  const current = currentId ? methodBaseId(currentId) : '';
  if (current && current !== 'comp') return current;
  const on = (Array.isArray(methods) ? methods : []).filter(isOn);
  if (on.length === 0 || on.some((m) => m.id === 'cash')) return 'cash';
  return on[0].id;
};

// Settings: the id a new custom method gets from its name.
export const customIdFor = (name) => String(name || '').trim().toLowerCase()
  .replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

// Why a new custom id cannot be used, or null when it can.
export const customIdError = (id) => {
  if (!id) return 'Enter a name using letters or digits';
  if (id.length > MAX_ID_LENGTH) return `A payment method name can be at most ${MAX_ID_LENGTH} characters`;
  if (BUILT_IN_IDS.includes(id)) return 'That is already a built-in payment method';
  if (RESERVED_IDS.includes(id)) return `"${id}" is a reserved word in SnowTrack; choose another name`;
  if (/_paid$/.test(id)) return 'A payment method name cannot end in "Paid"';
  if (id.includes('square')) return 'A custom payment method cannot contain "Square"; Square is built in';
  return null;
};

// Settings, Add: { methods } with the new method appended, or with a deleted
// method of the same id restored (same id, so its old stops and fee row match
// it again), or { error }.
export const addCustomMethod = (methods, name) => {
  const list = Array.isArray(methods) ? methods : [];
  const trimmed = String(name || '').trim();
  const id = customIdFor(trimmed);
  const existing = list.find((m) => m.id === id);
  if (existing && existing.deleted && !existing.isBuiltIn) {
    return {
      methods: list.map((m) => (m.id === id ? { ...m, name: trimmed, enabled: true, deleted: false } : m)),
    };
  }
  if (existing) return { error: 'A payment method with this name already exists' };
  const error = customIdError(id);
  if (error) return { error };
  return { methods: [...list, { id, name: trimmed, enabled: true, isBuiltIn: false }] };
};

// Settings, Delete: a soft delete. The entry stays, off and hidden, so its
// name still labels old stops and its fee row is saved off.
export const deleteCustomMethod = (methods, id) => (Array.isArray(methods) ? methods : [])
  .map((m) => (m.id === id && !m.isBuiltIn ? { ...m, enabled: false, deleted: true } : m));

// The methods Settings lists.
export const visibleMethods = (methods) => (Array.isArray(methods) ? methods : []).filter((m) => !m.deleted);

// History's payment column: "Paid - Zelle", "Invoice Sent" or "Not Paid".
export const paymentStatusLabel = (methods, status, method) => {
  if (status === 'paid' && method) return `Paid - ${methodLabel(methods, method)}`;
  if (status === 'invoice_sent') return 'Invoice Sent';
  return 'Not Paid';
};

// Reports, Revenue by Payment Method: the method's name, or "Not Specified".
// The server sends a stop with no method as the literal 'Not Specified'.
export const reportMethodLabel = (methods, method) => (
  !method || method === 'Not Specified' ? 'Not Specified' : methodLabel(methods, method)
);
